import React, { useEffect, useState } from "react";

import { filterOptionsWithSynonyms, generateRandomId, useTranslation } from "utils";

import { ExtraInfo, Form, Icon, InputNumber, KlimRadioGroup, OptGroup, Option, Select, Message as message } from "components";

export const FertilizerForm = ({ fertilizers, forms, index, product, year, onDelete, onUpdate, showErrors = true }) => {
  const { t } = useTranslation(["modules"]);
  const [showExplainerDrawer, setShowExplainerDrawer] = useState(false);
  const [applicationEnabled, setApplicationEnabled] = useState(true);
  const [fertlizerEnabled, setFertilizerEnabled] = useState(true);

  const setFormRef = year ? (el) => (forms.current[year][index] = el) : (el) => (forms.current[index] = el);

  const selectedFertilizerType = product.fertilizerType;
  const selectedFertilizer =
    fertilizers[selectedFertilizerType] && fertilizers[selectedFertilizerType].find((fertilizer) => fertilizer.id === product.fertilizerId);

  const rules = [{ required: true, message: false }];

  const selfProducedOptions = [
    { label: t("bauEntryDrawer.fertilizer.selfProduced.yes"), value: true },
    { label: t("bauEntryDrawer.fertilizer.selfProduced.no"), value: false },
  ];

  useEffect(() => {
    if (product?.fertilizerId && fertilizers[product.fertilizerType]) {
      const productDetails = fertilizers[product.fertilizerType]?.find((item) => item.id === product.fertilizerId);
      setFertilizerEnabled(productDetails?.enabled);

      if (product.applicationType) {
        const applicationTypeDetails = fertilizers.applications?.find((item) => item.id === product.applicationType);
        setApplicationEnabled(applicationTypeDetails?.enabled);
      }
    }
  }, [product, fertilizers]);

  useEffect(() => {
    if (!fertlizerEnabled || !applicationEnabled) {
      message({ content: t("bauEntryDrawer.updateRequiredMessage"), icon: "error" });
    }
  }, [fertlizerEnabled, applicationEnabled, t]);

  return (
    <>
      <div className="form-product">
        <div className="form-product-header">
          <div className="icon-row margin-bottom-0">
            <h6>{t("bauEntryDrawer.fertilizer.product", { number: index + 1 })}</h6>
            <Icon
              iconName="tabler/info-circle"
              className="inline margin-left-25 pointer"
              mixpanelText={t("bauEntryDrawer.fertilizer.product")}
              size="s"
              onClick={() => setShowExplainerDrawer(true)}
            />
          </div>
          <Icon dataTestId="fertilizer-form-trash" iconName="tabler/trash" onClick={() => onDelete(index)} />
        </div>
        <Form
          name={year ? `existing-${year}-${index}-${generateRandomId()}` : `existing-${index}-${generateRandomId()}`}
          ref={setFormRef}
          onValuesChange={(changed, v) => {
            if (Object.keys(changed).includes("fertilizerType")) {
              // we need to reset certain pieces of state
              const form = year ? forms.current[year][index] : forms.current[index];
              form.setFieldsValue({
                fertilizerId: null,
                applicationType: null,
              });
              onUpdate(index, {
                ...v,
                fertilizerId: null,
                applicationType: null,
              });
              return;
            }
            onUpdate(index, v);
          }}
          className={!showErrors ? "no-errors" : ""}
        >
          <div className="form-input-container">
            <Select
              label={t("bauEntryDrawer.fertilizer.fertilizerType")}
              name="fertilizerType"
              initialValue={product.fertilizerType}
              rules={rules}
              showSearch
              optionFilterProp="children"
            >
              <Option value="mineral">{t("bauEntryDrawer.fertilizer.mineral")}</Option>
              <Option value="organic">{t("bauEntryDrawer.fertilizer.organic")}</Option>
            </Select>
            <Select
              label={t("bauEntryDrawer.fertilizer.productName")}
              name="fertilizerId"
              disabled={!selectedFertilizerType}
              initialValue={product.fertilizerId}
              rules={rules}
              showSearch
              optionFilterProp="children"
              filterOption={filterOptionsWithSynonyms}
              className={!fertlizerEnabled ? "disabled-property" : ""}
            >
              {selectedFertilizerType && fertilizers.top[selectedFertilizerType].length && (
                <OptGroup key="top" label={t("bauEntryDrawer.fertilizer.topFertilizers")}>
                  {fertilizers.top[selectedFertilizerType].map((fertilizer) => (
                    <Option key={`top-${fertilizer.id}`} label={fertilizer.synonyms} value={fertilizer.id}>
                      {fertilizer.name}
                    </Option>
                  ))}
                </OptGroup>
              )}
              <OptGroup key="all" label={t("bauEntryDrawer.fertilizer.allFertilizers")}>
                {selectedFertilizerType &&
                  fertilizers[selectedFertilizerType]
                    .sort((a, b) => a.name?.toString().localeCompare(b.name?.toString()))
                    // If the initial value is of a disabled element we want to render it,
                    // all other disabled ones not
                    .filter((fertilizer) => fertilizer.enabled || fertilizer.id === product.fertilizerId)
                    .map((fertilizer) => (
                      <Option key={`all-${fertilizer.id}`} label={fertilizer.synonyms} value={fertilizer.id}>
                        {fertilizer.name}
                      </Option>
                    ))}
              </OptGroup>
            </Select>
            {!fertlizerEnabled && <span className="disabled-property-message">{t("bauEntryDrawer.fertilizer.disabledMessageProduct")}</span>}

            {selectedFertilizerType === "organic" && (
              <>
                <InputNumber
                  initialValue={product.nValue}
                  label={t("bauEntryDrawer.fertilizer.nValue")}
                  name="nValue"
                  decimalSeparator=","
                  min={0.01}
                  rules={rules}
                  step={0.01}
                  suffix={
                    selectedFertilizerType === "organic"
                      ? selectedFertilizer && selectedFertilizer.type === "manure"
                        ? t("bauEntryDrawer.fertilizer.manureNValueSuffix")
                        : t("bauEntryDrawer.fertilizer.dungNValueSuffix")
                      : t("bauEntryDrawer.fertilizer.nValueSuffix")
                  }
                />
                <Select
                  label={t("bauEntryDrawer.fertilizer.applicationType")}
                  name="applicationType"
                  initialValue={product.applicationType}
                  rules={rules}
                  showSearch
                  optionFilterProp="children"
                  filterOption={filterOptionsWithSynonyms}
                  className={!applicationEnabled ? "disabled-property" : ""}
                >
                  {fertilizers.applications
                    // If the initial value is of a disabled element we want to render it,
                    // all other disabled ones not
                    .filter((application) => application.enabled || application.id === product.applicationType)
                    .map((application) => (
                      <Option key={application.id} label={application.synonyms} value={application.id}>
                        {application.name}
                      </Option>
                    ))}
                </Select>
                {!applicationEnabled && <span className="disabled-property-message">{t("bauEntryDrawer.fertilizer.disabledMessageApplication")}</span>}
              </>
            )}
            <InputNumber
              label={t("bauEntryDrawer.fertilizer.amount")}
              name="amount"
              decimalSeparator=","
              disabled={!selectedFertilizerType}
              initialValue={product.amount}
              min={0.01}
              rules={rules}
              step={0.01}
              suffix={
                selectedFertilizerType === "organic"
                  ? selectedFertilizer && selectedFertilizer.type === "manure"
                    ? t("bauEntryDrawer.fertilizer.manureAmountSuffix")
                    : t("bauEntryDrawer.fertilizer.dungAmountSuffix")
                  : t("bauEntryDrawer.fertilizer.mineralAmountSuffix")
              }
            />
            {selectedFertilizerType === "organic" && (
              <div className="row-space-between">
                <span className="paragraph-m">{t("bauEntryDrawer.fertilizer.selfProduced.title")}</span>
                <Form.Item initialValue={product.selfProduced || false} name="selfProduced">
                  <KlimRadioGroup
                    type="tertiary"
                    size="large"
                    data-testid="fertilizer-form-self-produced-radio-button"
                    options={selfProducedOptions}
                    value={product?.selfProduced}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
              </div>
            )}
          </div>
        </Form>
      </div>
      <ExtraInfo type="fertilizer-explainer" visible={showExplainerDrawer} setVisible={setShowExplainerDrawer} />
    </>
  );
};
